//External
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  EyeIcon,
  EyeOffIcon,
  ExclamationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/solid';
import { CheckIcon } from '@heroicons/react/outline';
//Internal

import useNotifier from '../../hooks/useNotifier';
import Logo from '../../assets/logos/CL-Logo-Transparent-260x60.png';
import { Link } from 'react-router-dom';

//Component interface
interface IProps {
  onRegister: Function;
  failedRegister: boolean;
  setFailedRegister: (value: boolean) => void;
  success: boolean;
}

//Form interface
interface IFormInputs {
  fullName: string;
  username: string;
  password: string;
  confirmPassword: string;
}

//Form validation
const validationSchema = yup.object().shape({
  fullName: yup.string().max(255).required('Full name is required'),
  username: yup
    .string()
    .max(255)
    .email('Must be a valid email')
    .required('Username is required'),
});

// Component
const RegisterForm: React.FC<IProps> = ({
  onRegister,
  failedRegister,
  setFailedRegister,
  success,
}) => {
  const [viewPassword, setViewPassword] = useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);

  // RegisterForm should start blank
  const loadEmptyValues: IFormInputs = {
    fullName: '',
    username: '',
    password: '',
    confirmPassword: '',
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormInputs>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: loadEmptyValues,
  });
  // Handlers
  const onSubmit = (values: IFormInputs) => {
    //console.log('RegisterForm:onSubmit:values: ', values);
    onRegister(values);
    reset();
  };

  useEffect(() => {
    if (success) {
      setShowSuccessMessage(true);
    }
  }, [success]);

  return (
    <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
        {showSuccessMessage ? (
          <div>
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
              <CheckIcon
                className="h-6 w-6 text-green-600"
                aria-hidden="true"
              />
            </div>
            <div className="mt-3 text-center sm:mt-5">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Account Created
              </h3>
              <div className="mt-7 mx-auto max-w-md">
                <p className="text-xl text-gray-500">
                  We've sent you an email with a link to activate your account.
                </p>
              </div>
              <div className="mt-7 mx-auto max-w-xs text-sm">
                <Link
                  to="/login"
                  className="font-medium text-primary hover:text-primary-light"
                >
                  Back to login
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-6">
              <InputGroup label="Full Name">
                <InputField
                  type="text"
                  {...register(`fullName`)}
                  error={errors.fullName as any}
                />
              </InputGroup>
              <InputGroup label="Email">
                <InputField
                  type="text"
                  {...register(`username`)}
                  error={errors.username as any}
                />
              </InputGroup>
            </div>

            <div className="mt-10 flex flex-col justify-center w-3/4 mx-auto">
              <Button
                variant="primary"
                type="submit"
                disabled={
                  !!(errors?.fullName as any) || !!(errors?.username as any)
                }
              >
                <span>Create Account</span>
              </Button>
            </div>
            {failedRegister && (
              <div className="mt-8 rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <XCircleIcon
                      className="h-5 w-5 text-red-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      There was an error creating your account
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <ul role="list" className="list-disc space-y-1 pl-5">
                        <li>Do you already have an account?</li>
                        <li>Contact support@cloudlistings.com for help</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="text-sm mt-10">
              <Link
                to="/login"
                className="font-medium text-primary hover:text-primary-light"
              >
                Already have an account?
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default RegisterForm;

//////
// Abstraction of components created during Profile JSX cleanup
//
/**
 * TODO: Create/Move to /components/InputGroup.tsx and import
 */
const InputGroup = ({
  label,
  children,
  ...props
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <label {...props} className="block text-sm font-medium text-gray-700">
      {label}
    </label>
    {children}
  </div>
);
/**
 * TODO: Create/Move to /components/InputField.tsx and import
 */
const InputField = React.forwardRef<
  HTMLInputElement,
  {
    error?: any;
    [x: string]: any;
  }
>(({ error, ...props }, ref) => (
  <div className="relative">
    <input
      ref={ref}
      {...props}
      className={`${
        error
          ? `border-red-300 placeholder-red-300 focus:ring-red-500 focus:border-red-500`
          : `border-gray-300 placeholder-gray-400 focus:ring-primary-light focus:border-primary-light`
      } mt-1 appearance-none block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none sm:text-sm`}
    />
    {error ? (
      <div className="absolute inset-y-0 -top-7 right-0 pr-3 flex items-center pointer-events-none">
        <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
      </div>
    ) : null}
    <p className="mt-2 text-sm text-red-600">{error?.message}</p>
  </div>
));
/**
 * TODO: Create/Move to /components/Button.tsx and import
 */
// MB comment: Based on Mark's button as per Profile.tsx pre- bug/45
// Enhancements: added the "disabled" attribute with disabled logic and associated UI feedbacks
// Currently, only variant=secondary is fully tested, the rest of variants are not tested yet.
const Button = ({
  variant,
  type = 'button',
  disabled = false,
  children,
  className = '',
  onClick,
}: {
  variant: string;
  type: 'button' | 'submit';
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
  onClick?: any;
}) => {
  return variant === 'primary' ? (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${
        disabled
          ? 'cursor-not-allowed hover:bg-primary'
          : 'hover:bg-primary-dark'
      }  bg-primary border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light ${className}`}
    >
      {children}
    </button>
  ) : variant === 'secondary' ? (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${
        disabled ? 'cursor-not-allowed hover:bg-white ' : 'hover:bg-gray-50'
      }  bg-white inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light ${className}`}
    >
      {children}
    </button>
  ) : variant === 'primary icon' ? (
    <button
      type={type}
      className={`${
        disabled
          ? 'cursor-not-allowed hover:bg-primary'
          : 'hover:bg-primary-dark'
      }  bg-primary border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  ) : variant === 'secondary icon' ? (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${
        disabled ? 'cursor-not-allowed hover:bg-white' : 'hover:bg-gray-50'
      }  bg-white inline-flex items-center px-2 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light ${className}`}
    >
      {children}
    </button>
  ) : null;
};
